import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable, {
  headerCellStyle,
  bodyCellStyle,
} from "./components/SelectableEnrollmentTable";

interface BulkIssueTcModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "issued" | "skipped" | "not_found" | "error";
  previousStatusId?: number | null;
  newStatusId?: number;
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    issued:    { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkIssueTcModal: React.FC<BulkIssueTcModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const [statuses, setStatuses] = useState<Array<{ id: number; name: string }>>([]);
  const [tcStatuses, setTcStatuses] = useState<Array<{ id: number; name: string }>>([]);
  const [tcReasons, setTcReasons] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [statusId, setStatusId] = useState<number | "">("");
  const [tcStatusId, setTcStatusId] = useState<number | "">("");
  const [tcReasonId, setTcReasonId] = useState<number | "">("");
  const [date, setDate] = useState("");
  const [remarks, setRemarks] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data?.data || {};
        setStatuses(d.statuses || []);
        setTcStatuses(d.tcStatuses || []);
        setTcReasons(d.tcReasons || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const handleClose = () => {
    setStatusId("");
    setTcStatusId("");
    setTcReasonId("");
    setDate("");
    setRemarks("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!statusId) return setError("Pick a status to change to.");
    if (!tcReasonId) return setError("Pick a reason for issue.");
    if (!tcStatusId) return setError("Pick a TC status.");
    if (!date) return setError("Date is required.");
    if (!remarks.trim()) return setError("Remarks are required.");
    if (selectedEnrollmentIds.length === 0) {
      return setError("Select at least one enrollment.");
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkIssueTc({
        enrollmentIds: selectedEnrollmentIds,
        statusId: Number(statusId),
        tcStatusId: Number(tcStatusId),
        tcReasonId: Number(tcReasonId),
        date: date || null,
        remarks: remarks.trim(),
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk issue TC failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results
            ? "Issue Transfer Certificate — Results"
            : "Issue Transfer Certificate"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: 1, minWidth: 200 }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Change Status To <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={statusId}
                      onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">Select status…</option>
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" style={{ flex: 1, minWidth: 200 }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Reason for Issue <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={tcReasonId}
                      onChange={(e) => setTcReasonId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">Select reason…</option>
                      {tcReasons.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: 1, minWidth: 200 }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      TC Status <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={tcStatusId}
                      onChange={(e) => setTcStatusId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">Select TC status…</option>
                      {tcStatuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" style={{ width: 180 }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Date <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Remarks <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Recorded in the status audit trail"
                  />
                </Form.Group>

                <div
                  style={{
                    fontSize: 12,
                    color: "#854d0e",
                    background: "#fefce8",
                    border: "1px solid var(--status-warning-border)",
                    borderRadius: 6,
                    padding: "6px 10px",
                    marginBottom: 10,
                  }}
                >
                  Issuing a TC records a status-audit entry per enrollment. The enrollment's own
                  status is not changed (mirrors the legacy behavior).
                </div>

                <SelectableEnrollmentTable
                  enrollments={enrollments}
                  selectedIds={selectedIds}
                  allSelected={allSelected}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  disabled={submitting}
                  caption={`Selected Students (${selectedEnrollmentIds.length} of ${enrollments.length} selected)`}
                />
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.issued ?? 0}</strong> issued
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "issued" && (
                          <span style={{ color: "var(--text-body)" }}>TC audit recorded</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_at_target_status"
                              ? "Already at target status"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                loadingLookups ||
                !statusId ||
                !tcStatusId ||
                !tcReasonId ||
                !date ||
                !remarks.trim() ||
                selectedEnrollmentIds.length === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Submitting…
                </>
              ) : (
                <>Submit</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkIssueTcModal;
